/**
 * NewMeetingScreen — flujo de creación / continuación de un procesamiento.
 *
 * Maneja DOS entry points:
 *
 *   A. Creación nueva: currentProcessingIdAtom = null → formulario.
 *   B. Continuación: currentProcessingIdAtom = "<id>" → retoma según status.
 *
 * Flujos internos:
 *   form → creating → uploading → starting_transcription → transcribing
 *   → finalizing → loading_files → [navigate a identify]
 *
 * Cancel transcripción (Fase 13.C):
 *   En estado "transcribing", el usuario puede cancelar con confirmación
 *   inline. Al confirmar: llama DELETE /api/processings/:id/transcribe,
 *   backend cancela el job en OCI y marca meta como "failed", frontend
 *   navega a Meetings. El poll timeout se limpia antes de navegar.
 *
 * Audio en continuación (Fase 13.A):
 *   hasLocalAudioRef rastrea si hay File en memoria para evitar fetch
 *   innecesario de la PAR de audio.wav en el flujo nuevo.
 *
 * Rehidratación de estado del usuario (Fase 14):
 *   Al llegar a loadFilesAndGoToIdentify con un meeting existente, después
 *   de descargar transcript.json, hace GET /api/processings/:id y rehidrata
 *   profilesAtom, customSpeakersAtom, manualOverridesAtom, thresholdAtom
 *   y metaVersionAtom desde el meta.json.
 *   Si el GET falla, el usuario empieza con el estado en blanco (no fatal).
 *
 * Fase 18: solo se descarga transcript.json. El SRT de OCI ya no se usa
 *   en el frontend — la segmentación viene de segmentFromTokens.
 */

import { useEffect, useRef, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import styled from "styled-components";
import {
  audioFileAtom,
  audioFilenameAtom,
  audioSrcAtom,
  currentProcessingIdAtom,
  customSpeakersAtom,
  manualOverridesAtom,
  metaVersionAtom,
  oracleJsonAtom,
  profilesAtom,
  thresholdAtom,
} from "../../state/atoms";
import {
  ApiError,
  cancelTranscription,
  createProcessing,
  fetchTextFromPar,
  finalizeProcessing,
  getProcessing,
  getProcessingFile,
  getProcessingStatus,
  transcribeProcessing,
  uploadToPar,
  type StatusResponse,
  type UploadHandle,
  type UploadProgress,
} from "../../platform/api";
import { parseOracleTranscription } from "../../lib/parse-oracle";
import {
  MAX_SPEAKERS,
  MIN_SPEAKERS,
  SUPPORTED_LANGUAGES,
} from "../../lib/types";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { Stack } from "../components/Stack";
import { Input } from "../components/Input";
import { useNavigateTo, useSyncParams } from "../routing";

// ---------------------------------------------------------------------------
// Estados internos del flow.
// ---------------------------------------------------------------------------

type FlowState =
  | { kind: "form" }
  | { kind: "creating" }
  | { kind: "uploading"; processingId: string; progress: UploadProgress }
  | { kind: "starting_transcription"; processingId: string }
  | { kind: "transcribing"; processingId: string; lastStatus: StatusResponse | null }
  | { kind: "finalizing"; processingId: string }
  | { kind: "loading_files"; processingId: string }
  | { kind: "failed"; processingId: string | null; message: string };

const POLL_INTERVAL_INITIAL_MS = 15_000;
const POLL_INTERVAL_MAX_MS = 60_000;
const POLL_RAMP_AFTER_MS = 5 * 60_000;

export function NewMeetingScreen() {
  const navigateTo = useNavigateTo();
  useSyncParams();
  const setOracleJson = useSetAtom(oracleJsonAtom);
  const setAudioFile = useSetAtom(audioFileAtom);
  const setAudioFilename = useSetAtom(audioFilenameAtom);
  const setAudioSrc = useSetAtom(audioSrcAtom);
  const setCurrentProcessingId = useSetAtom(currentProcessingIdAtom);
  const incomingProcessingId = useAtomValue(currentProcessingIdAtom);

  // Fase 14 — atoms de estado del usuario para rehidratación.
  const setProfiles = useSetAtom(profilesAtom);
  const setCustomSpeakers = useSetAtom(customSpeakersAtom);
  const setManualOverrides = useSetAtom(manualOverridesAtom);
  const setThreshold = useSetAtom(thresholdAtom);
  const setMetaVersion = useSetAtom(metaVersionAtom);

  const [flow, setFlow] = useState<FlowState>(() =>
    incomingProcessingId === null
      ? { kind: "form" }
      : { kind: "transcribing", processingId: incomingProcessingId, lastStatus: null }
  );

  // Form fields.
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState<string>("es");
  const [numberOfSpeakers, setNumberOfSpeakers] = useState<number>(2);
  const [file, setFile] = useState<File | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Cancel transcription state.
  const [cancelConfirming, setCancelConfirming] = useState(false);
  const [canceling, setCanceling] = useState(false);

  // Refs.
  const uploadHandleRef = useRef<UploadHandle | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollStartedAtRef = useRef<number>(0);
  const aliveRef = useRef(true);
  const hasLocalAudioRef = useRef(false);
  const resumeStartedRef = useRef(false);

  useEffect(() => {
    return () => {
      aliveRef.current = false;
      uploadHandleRef.current?.abort();
      if (pollTimeoutRef.current !== null) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (incomingProcessingId === null || resumeStartedRef.current) return;
    resumeStartedRef.current = true;
    void resumeProcessing(incomingProcessingId);
  }, [incomingProcessingId]);

  async function resumeProcessing(id: string) {
    try {
      const status = await getProcessingStatus(id);
      if (!aliveRef.current) return;
      routeFromStatus(id, status);
    } catch (err) {
      if (!aliveRef.current) return;
      setFlow({
        kind: "failed",
        processingId: id,
        message: err instanceof ApiError ? err.message : "No se pudo consultar el estado del procesamiento.",
      });
    }
  }

  function routeFromStatus(id: string, status: StatusResponse) {
    switch (status.status) {
      case "ready":
      case "tagged":
        void loadFilesAndGoToIdentify(id, hasLocalAudioRef.current);
        return;
      case "ready_to_finalize":
        void runFinalize(id);
        return;
      case "transcribing":
        setFlow({ kind: "transcribing", processingId: id, lastStatus: status });
        schedulePoll(id);
        return;
      case "uploading":
        setFlow({
          kind: "failed",
          processingId: id,
          message: "Este procesamiento no completó la subida del audio. Por ahora no se puede continuar desde la app; tendrías que volver a crearlo.",
        });
        return;
      case "failed":
        setFlow({
          kind: "failed",
          processingId: id,
          message: status.lifecycleDetails ?? "El job de transcripción falló en OCI. Volvé a crear el procesamiento.",
        });
        return;
    }
  }

  async function handleSubmit() {
    setFormError(null);
    const validation = validateForm({ title, file });
    if (!validation.ok) { setFormError(validation.error); return; }

    setFlow({ kind: "creating" });

    let create;
    try {
      create = await createProcessing({
        title: title.trim(),
        filename: validation.file.name,
        fileSize: validation.file.size,
        language,
        numberOfSpeakers,
      });
    } catch (err) {
      if (!aliveRef.current) return;
      setFlow({ kind: "failed", processingId: null, message: err instanceof ApiError ? err.message : "No se pudo crear el procesamiento." });
      return;
    }
    if (!aliveRef.current) return;

    setCurrentProcessingId(create.processingId);
    setAudioFile(validation.file);
    setAudioFilename(validation.file.name.toLowerCase().endsWith(".mp4") ? "audio.mp4" : "audio.wav");
    hasLocalAudioRef.current = true;

    setFlow({ kind: "uploading", processingId: create.processingId, progress: { loaded: 0, total: validation.file.size, fraction: 0 } });

    const handle = uploadToPar(create.uploadParUrl, validation.file, (progress) => {
      if (!aliveRef.current) return;
      setFlow((prev) => prev.kind === "uploading" ? { ...prev, progress } : prev);
    });
    uploadHandleRef.current = handle;

    try {
      await handle.done;
    } catch (err) {
      if (!aliveRef.current) return;
      const isAbort = err instanceof Error && err.name === "AbortError";
      setFlow({ kind: "failed", processingId: create.processingId, message: isAbort ? "Subida cancelada." : err instanceof ApiError ? err.message : "Falló la subida del audio." });
      return;
    }
    uploadHandleRef.current = null;

    setFlow({ kind: "starting_transcription", processingId: create.processingId });
    try {
      await transcribeProcessing(create.processingId);
    } catch (err) {
      if (!aliveRef.current) return;
      setFlow({ kind: "failed", processingId: create.processingId, message: err instanceof ApiError ? err.message : "No se pudo iniciar la transcripción." });
      return;
    }
    if (!aliveRef.current) return;

    setFlow({ kind: "transcribing", processingId: create.processingId, lastStatus: null });
    pollStartedAtRef.current = Date.now();
    schedulePoll(create.processingId);
  }

  function schedulePoll(id: string) {
    if (pollStartedAtRef.current === 0) pollStartedAtRef.current = Date.now();
    const elapsed = Date.now() - pollStartedAtRef.current;
    const interval = elapsed < POLL_RAMP_AFTER_MS ? POLL_INTERVAL_INITIAL_MS : POLL_INTERVAL_MAX_MS;

    pollTimeoutRef.current = setTimeout(async () => {
      if (!aliveRef.current) return;
      try {
        const status = await getProcessingStatus(id);
        if (!aliveRef.current) return;
        if (status.status === "ready_to_finalize") { void runFinalize(id); return; }
        if (status.status === "ready" || status.status === "tagged") { void loadFilesAndGoToIdentify(id, hasLocalAudioRef.current); return; }
        if (status.status === "failed") {
          setFlow({ kind: "failed", processingId: id, message: status.lifecycleDetails ?? "El job de transcripción falló en OCI." });
          return;
        }
        setFlow({ kind: "transcribing", processingId: id, lastStatus: status });
        schedulePoll(id);
      } catch (err) {
        if (!aliveRef.current) return;
        if (err instanceof ApiError && (err.status === 404 || err.status === 401)) {
          setFlow({ kind: "failed", processingId: id, message: err.message });
          return;
        }
        schedulePoll(id);
      }
    }, interval);
  }

  async function runFinalize(id: string) {
    setFlow({ kind: "finalizing", processingId: id });
    try {
      await finalizeProcessing(id);
    } catch (err) {
      if (!aliveRef.current) return;
      setFlow({ kind: "failed", processingId: id, message: err instanceof ApiError ? err.message : "No se pudo finalizar el procesamiento." });
      return;
    }
    if (!aliveRef.current) return;
    void loadFilesAndGoToIdentify(id, hasLocalAudioRef.current);
  }

  async function loadFilesAndGoToIdentify(id: string, hasLocalAudio: boolean) {
    setFlow({ kind: "loading_files", processingId: id });
    try {
      // Transcript y meta en paralelo: ambos son necesarios antes de navegar.
      // El meta falla silencioso — si no está disponible, el usuario empieza
      // con estado en blanco y puede volver a asignar perfiles.
      const [transcriptInfo, metaResult] = await Promise.all([
        getProcessingFile(id, "transcript.json"),
        getProcessing(id).catch((err) => {
          console.warn("[loadFilesAndGoToIdentify] getProcessing falló, iniciando sin estado guardado:", err);
          return null;
        }),
      ]);

      const transcriptText = await fetchTextFromPar(transcriptInfo.downloadUrl);

      // Nombre normalizado del audio en el bucket. Retrocompat: metas sin el
      // campo (anteriores a Fase 21) defaultean a "audio.wav".
      const audioFilename = metaResult?.audioFilename ?? "audio.wav";
      setAudioFilename(audioFilename);

      if (!hasLocalAudio) {
        try {
          const audioInfo = await getProcessingFile(id, audioFilename);
          setAudioSrc({ url: audioInfo.downloadUrl, expiresAt: new Date(audioInfo.expiresAt).getTime() });
        } catch {
          console.warn(`[loadFilesAndGoToIdentify] ${audioFilename} no encontrado para`, id);
        }
      }

      const oracle = parseOracleTranscription(transcriptText);
      if (!aliveRef.current) return;

      setOracleJson(oracle);

      // Rehidratación del estado del usuario desde meta.json.
      if (metaResult) {
        // Versión para conflict resolution en el próximo autoguardado.
        setMetaVersion(metaResult.version ?? 0);

        // speakerProfiles: Record<number, WireSpeakerProfile> → Map<number, SpeakerProfile>
        if (metaResult.speakerProfiles) {
          const profilesMap = new Map(
            Object.entries(metaResult.speakerProfiles).map(([k, v]) => {
              const idx = Number(k);
              return [idx, { ...v, index: idx }];
            })
          );
          setProfiles(profilesMap);
        }

        // customSpeakers: number[] — no requiere conversión.
        if (metaResult.customSpeakers && metaResult.customSpeakers.length > 0) {
          setCustomSpeakers(metaResult.customSpeakers);
        }

        // manualOverrides: Record<number, number> → Map<number, number>
        if (metaResult.manualOverrides) {
          const overridesMap = new Map(
            Object.entries(metaResult.manualOverrides).map(([k, v]) => [Number(k), Number(v)])
          );
          setManualOverrides(overridesMap);
        }

        // threshold: usar solo si está presente en el meta.
        if (metaResult.threshold !== undefined) {
          setThreshold(metaResult.threshold);
        }
      }

      navigateTo.identify(id);
    } catch (err) {
      if (!aliveRef.current) return;
      setFlow({ kind: "failed", processingId: id, message: err instanceof ApiError ? err.message : err instanceof Error ? `Error procesando los archivos: ${err.message}` : "Error procesando los archivos del procesamiento." });
    }
  }

  function handleBack() { navigateTo.meetings(); }
  function handleAbortUpload() { uploadHandleRef.current?.abort(); }

  // ----- Cancel transcription -----

  function handleCancelRequest() { setCancelConfirming(true); }
  function handleCancelAbort() { setCancelConfirming(false); }

  async function handleCancelConfirm() {
    if (flow.kind !== "transcribing") return;
    const id = flow.processingId;
    setCancelConfirming(false);
    setCanceling(true);
    if (pollTimeoutRef.current !== null) clearTimeout(pollTimeoutRef.current);
    try {
      await cancelTranscription(id);
    } catch {
      // best-effort: si falla el cancel en OCI, igual navegamos.
    }
    if (!aliveRef.current) return;
    setCanceling(false);
    navigateTo.meetings();
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={handleBack} aria-label="Volver a Reuniones">
          ← Reuniones
        </BackButton>
        <Title>Nueva reunión</Title>
        <div />
      </Header>

      {flow.kind === "form" && (
        <FormView title={title} setTitle={setTitle} language={language} setLanguage={setLanguage}
          numberOfSpeakers={numberOfSpeakers} setNumberOfSpeakers={setNumberOfSpeakers}
          file={file} setFile={setFile} formError={formError} onSubmit={handleSubmit} />
      )}
      {flow.kind === "creating" && <Status>Creando procesamiento…</Status>}
      {flow.kind === "uploading" && (
        <UploadingView progress={flow.progress} onAbort={handleAbortUpload} />
      )}
      {flow.kind === "starting_transcription" && <Status>Iniciando transcripción…</Status>}
      {flow.kind === "transcribing" && (
        <TranscribingView
          lastStatus={flow.lastStatus}
          cancelConfirming={cancelConfirming}
          canceling={canceling}
          onCancelRequest={handleCancelRequest}
          onCancelConfirm={() => void handleCancelConfirm()}
          onCancelAbort={handleCancelAbort}
        />
      )}
      {flow.kind === "finalizing" && <Status>Finalizando… moviendo archivos al folder definitivo.</Status>}
      {flow.kind === "loading_files" && <Status>Descargando transcripción…</Status>}
      {flow.kind === "failed" && <FailedView message={flow.message} onBack={handleBack} />}
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Sub-views
// ---------------------------------------------------------------------------

function FormView(props: {
  title: string; setTitle: (v: string) => void;
  language: string; setLanguage: (v: string) => void;
  numberOfSpeakers: number; setNumberOfSpeakers: (v: number) => void;
  file: File | null; setFile: (f: File | null) => void;
  formError: string | null; onSubmit: () => void;
}) {
  return (
    <Card>
      <Stack $direction="column" $gap="md">
        <Field>
          <Label htmlFor="title">Título</Label>
          <Input id="title" type="text" value={props.title}
            onChange={(e) => props.setTitle(e.target.value)}
            placeholder="Reunión de equipo Q3" maxLength={200} />
          <Hint>Hasta 200 caracteres.</Hint>
        </Field>
        <Field>
          <Label htmlFor="language">Idioma</Label>
          <Select id="language" value={props.language}
            onChange={(e) => props.setLanguage(e.target.value)}>
            {SUPPORTED_LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </Select>
          <Hint>"Detección automática" funciona bien con reuniones que mezclan idiomas.</Hint>
        </Field>
        <Field>
          <Label htmlFor="speakers">Número de participantes</Label>
          <Input id="speakers" type="number" min={MIN_SPEAKERS} max={MAX_SPEAKERS}
            value={props.numberOfSpeakers}
            onChange={(e) => { const n = Number(e.target.value); if (Number.isInteger(n)) props.setNumberOfSpeakers(n); }} />
          <Hint>Entre {MIN_SPEAKERS} y {MAX_SPEAKERS}. Hint para la diarización.</Hint>
        </Field>
        <Field>
          <Label htmlFor="wav">Archivo de audio (.wav, .mp4)</Label>
          <FileInputWrapper>
            <input id="wav" type="file" accept="audio/wav,audio/x-wav,.wav,video/mp4,.mp4"
              onChange={(e) => props.setFile(e.target.files?.[0] ?? null)} />
            {props.file && <FileInfo>{props.file.name} — {formatBytes(props.file.size)}</FileInfo>}
          </FileInputWrapper>
          <Hint>WAV o MP4 (Teams, Meet). Máximo 2 GB. La transcripción tarda ~1 minuto cada 10 minutos de audio.</Hint>
        </Field>
        {props.formError && <ErrorMessage>{props.formError}</ErrorMessage>}
        <Stack $direction="row" $gap="sm">
          <Button $variant="primary" onClick={props.onSubmit}>Crear y subir audio</Button>
        </Stack>
      </Stack>
    </Card>
  );
}

function UploadingView(props: { progress: UploadProgress; onAbort: () => void }) {
  const pct = Number.isFinite(props.progress.fraction) ? Math.round(props.progress.fraction * 100) : 0;
  return (
    <Card>
      <Stack $direction="column" $gap="md">
        <h3 style={{ margin: 0 }}>Subiendo audio</h3>
        <ProgressBarOuter><ProgressBarInner style={{ width: `${pct}%` }} /></ProgressBarOuter>
        <ProgressLabel>{pct}% — {formatBytes(props.progress.loaded)} / {formatBytes(props.progress.total)}</ProgressLabel>
        <div><Button $variant="ghost" onClick={props.onAbort}>Cancelar subida</Button></div>
      </Stack>
    </Card>
  );
}

function TranscribingView(props: {
  lastStatus: StatusResponse | null;
  cancelConfirming: boolean;
  canceling: boolean;
  onCancelRequest: () => void;
  onCancelConfirm: () => void;
  onCancelAbort: () => void;
}) {
  const pct = props.lastStatus?.percentComplete ?? null;
  const raw = props.lastStatus?.rawLifecycleState ?? null;
  return (
    <Card>
      <Stack $direction="column" $gap="md">
        <h3 style={{ margin: 0 }}>Transcribiendo…</h3>
        <p style={{ margin: 0 }}>
          OCI Speech está procesando tu audio. Podés cerrar esta pestaña y
          volver más tarde — el proceso continúa en el bucket. Cuando vuelvas,
          tocá esta reunión en la lista y retomamos desde donde estaba.
        </p>
        {pct !== null && (
          <>
            <ProgressBarOuter><ProgressBarInner style={{ width: `${pct}%` }} /></ProgressBarOuter>
            <ProgressLabel>{pct}%</ProgressLabel>
          </>
        )}
        {raw !== null && (
          <Meta>Estado del job: <code>{raw}</code></Meta>
        )}
        <CancelRow>
          {props.canceling ? (
            <CancelLabel>Cancelando…</CancelLabel>
          ) : props.cancelConfirming ? (
            <>
              <CancelLabel>¿Cancelar la transcripción?</CancelLabel>
              <Button $variant="primary" onClick={props.onCancelConfirm}>Sí, cancelar</Button>
              <Button $variant="ghost" onClick={props.onCancelAbort}>No, continuar</Button>
            </>
          ) : (
            <Button $variant="ghost" onClick={props.onCancelRequest}>
              Cancelar transcripción
            </Button>
          )}
        </CancelRow>
      </Stack>
    </Card>
  );
}

function FailedView(props: { message: string; onBack: () => void }) {
  return (
    <Card>
      <Stack $direction="column" $gap="md">
        <FailedTitle>Algo falló</FailedTitle>
        <p style={{ margin: 0 }}>{props.message}</p>
        <div><Button $variant="secondary" onClick={props.onBack}>Volver a Reuniones</Button></div>
      </Stack>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type ValidationResult = { ok: true; file: File } | { ok: false; error: string };

function validateForm(input: { title: string; file: File | null }): ValidationResult {
  const title = input.title.trim();
  if (title.length === 0) return { ok: false, error: "El título es obligatorio." };
  if (title.length > 200) return { ok: false, error: "El título supera los 200 caracteres." };
  if (input.file === null) return { ok: false, error: "Tenés que seleccionar un archivo .wav o .mp4." };
  const lowerName = input.file.name.toLowerCase();
  if (!lowerName.endsWith(".wav") && !lowerName.endsWith(".mp4")) {
    return { ok: false, error: "El archivo debe ser .wav o .mp4." };
  }
  if (input.file.size === 0) return { ok: false, error: "El archivo está vacío." };
  if (input.file.size > 2 * 1024 * 1024 * 1024) return { ok: false, error: "El archivo supera el límite de 2 GB de OCI Speech." };
  return { ok: true, file: input.file };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const Container = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.space.lg};
`;
const Header = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.space.lg};
`;
const Title = styled.h1`
  font-size: ${({ theme }) => theme.font.size.xxl};
  margin: 0;
  text-align: center;
`;
const BackButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.color.accent};
  cursor: pointer;
  font-size: ${({ theme }) => theme.font.size.md};
  padding: ${({ theme }) => theme.space.xs};
  justify-self: start;
  &:hover { text-decoration: underline; }
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
`;
const Label = styled.label`
  font-weight: ${({ theme }) => theme.font.weight.bold};
  font-size: ${({ theme }) => theme.font.size.md};
`;
const Hint = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;
const Select = styled.select`
  padding: ${({ theme }) => theme.space.sm};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.font.size.md};
  font-family: inherit;
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
`;
const FileInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
`;
const FileInfo = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.mono};
`;
const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.color.error};
  padding: ${({ theme }) => theme.space.sm};
  background: ${({ theme }) => theme.color.errorMuted};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.font.size.md};
`;
const Status = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.space.xl};
  color: ${({ theme }) => theme.color.textMuted};
  font-size: ${({ theme }) => theme.font.size.md};
`;
const ProgressBarOuter = styled.div`
  width: 100%;
  height: 8px;
  background: ${({ theme }) => theme.color.border};
  border-radius: 4px;
  overflow: hidden;
`;
const ProgressBarInner = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.color.accent};
  transition: width 0.2s ease;
`;
const ProgressLabel = styled.div`
  text-align: center;
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.mono};
`;
const Meta = styled.div`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
  code {
    font-family: ${({ theme }) => theme.font.mono};
    background: ${({ theme }) => theme.color.border};
    padding: 1px 6px;
    border-radius: 3px;
  }
`;
const CancelRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  padding-top: ${({ theme }) => theme.space.sm};
  border-top: 1px solid ${({ theme }) => theme.color.border};
  flex-wrap: wrap;
`;
const CancelLabel = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;
const FailedTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.color.error};
`;
